// Tarea 1: Refactor de ThemeProvider - solo maneja ThemeTokens
import React, { createContext, useContext, useLayoutEffect, useState } from 'react';
import { ThemeTokens, ThemeContextType } from '@/types/theme';
import { applyTokensToDOM, getDefaultTokens, isDarkTheme } from '@/lib/themeUtils';
import { getBootstrappedTheme } from '@/lib/themeCache';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const bootstrappedTheme = getBootstrappedTheme();
  const initialTokens = bootstrappedTheme?.tokens ?? getDefaultTokens();
  const [tokens, setTokens] = useState<ThemeTokens>(initialTokens);
  const [isThemeLoaded, setIsThemeLoaded] = useState(Boolean(bootstrappedTheme));
  const [isDark, setIsDark] = useState<boolean>(() => isDarkTheme(initialTokens));

  // Apply tokens to DOM when they change
  useLayoutEffect(() => {
    if (isThemeLoaded) {
      applyTokensToDOM(tokens);
      const dark = isDarkTheme(tokens);
      setIsDark(dark);
      document.documentElement.setAttribute('data-theme-tone', dark ? 'dark' : 'light');
    }
  }, [tokens, isThemeLoaded]);

  const applyTokens = (newTokens: ThemeTokens, _usingCachedTokens?: false) => {
    setTokens(newTokens);
    setIsThemeLoaded(true);
  };

  const setThemeLoaded = () => {
    setIsThemeLoaded(true);
  };

  const resetTheme = () => {
    setIsThemeLoaded(false);
  };

  const contextValue: ThemeContextType = {
    tokens,
    applyTokens,
    isThemeLoaded,
    isDarkTheme: isDark,
    setThemeLoaded,
    resetTheme
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
